import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { type ShareSection, sectionLabel } from './shareModel.js'
import type { Bilingual } from '../language/text.js'

/**
 * "Welke secties?" met Alle secties en Alleen bepaalde secties (opdracht van de productowner van 2 november
 * 2026, ontwerp 6.4, R-65, R-65a, R-65b, R-65c, R-140).
 *
 * Eén component voor uitnodigen (`ShareInvite`) en voor Rechten wijzigen bij een bestaande deling
 * (`AccessList`), zodat beide dezelfde woorden, hetzelfde aantal en dezelfde uitleg hebben. Het aantal is wat
 * nu in het formulier staat, zoals de teller en Alles kiezen in de lijst (`SectionPicker`); is er één sectie,
 * dan noemt de keuze haar bij naam. Onder Alle secties staat per reden wat alleen te lezen is en wat niet
 * meegaat, vóór het versturen (besluit 1). Staat er geen sectie in het formulier, dan is er niets te kiezen
 * en tekent dit component niets: de lijst eronder is dan de enige weg.
 */

export type Scope = 'alle' | 'bepaald'

export interface SectionScopeProps {
  readonly taal: Language
  /** Een unieke basis voor de naam van de keuzerondjes en de id van de uitleg. */
  readonly idBase: string
  readonly secties: readonly ShareSection[]
  readonly value: Scope
  readonly onChange: (next: Scope) => void
  /** De zinnen onder Alle secties (`allSectionsNotes`). */
  readonly notes: readonly string[]
}

const T = {
  secties: { nl: 'Welke secties?', en: 'Which sections?' },
  alle: { nl: 'Alle {n} secties', en: 'All {n} sections' },
  deEne: { nl: 'Sectie {s}', en: 'Section {s}' },
  bepaald: { nl: 'Alleen bepaalde secties', en: 'Only certain sections' },
} as const satisfies Readonly<Record<string, Bilingual>>

/** Hoeveel secties er nu in het formulier staan: het aantal achter Alle secties. */
export function shownSectionCount(sections: readonly ShareSection[]): number {
  return sections.filter((section) => section.verborgen !== true).length
}

export function SectionScope({ taal: language, idBase, secties: sections, value, onChange, notes }: SectionScopeProps): ReactNode {
  const shown = sections.filter((section) => section.verborgen !== true)
  if (shown.length === 0) return null
  const only = shown.length === 1 ? shown[0] : undefined
  const notesId = `${idBase}-alle-uitleg`
  return (
    <fieldset className="ef-keuzegroep ef-reikwijdte">
      <legend className="ef-veld__label">
        <span className="ef-veld__labeltekst">{T.secties[language]}</span>
      </legend>
      <div className="ef-reikwijdte__keuzes">
        <label className="ef-keuze ef-reikwijdte__keuze">
          <input
            type="radio"
            name={`${idBase}-reikwijdte`}
            value="alle"
            checked={value === 'alle'}
            {...(notes.length === 0 ? {} : { 'aria-describedby': notesId })}
            onChange={() => {
              onChange('alle')
            }}
          />
          <span>{only === undefined ? T.alle[language].replace('{n}', String(shown.length)) : T.deEne[language].replace('{s}', sectionLabel(only))}</span>
        </label>
        <label className="ef-keuze ef-reikwijdte__keuze">
          <input
            type="radio"
            name={`${idBase}-reikwijdte`}
            value="bepaald"
            checked={value === 'bepaald'}
            onChange={() => {
              onChange('bepaald')
            }}
          />
          <span>{T.bepaald[language]}</span>
        </label>
      </div>
      {value === 'alle' && notes.length > 0 ? (
        <ul id={notesId} className="ef-reikwijdte__uitleg">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </fieldset>
  )
}
